import { Box, Button, TextField } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { OpenAIResponse } from "../../rule-engine-core/Plugins/OpenAPI/OpenAIResponse";
import { AppGlueCorePlugins } from "../../rule-engine-core/AppGlueCorePlugins";
import { UILabel } from "../BuildlingBlocks/UILabel";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";

export function OpenAIChatUI(props: {
    pretext?: string;
    posttext?: string;
    label?: string;
    initialPrompt?: string;
    onSelectAnswer?: (answer: string) => void;
    priorConversation?: OpenAIResponse<string>;
    needsClean?: (text: string) => boolean;
}) {
    let [prompt, setPrompt] = useState<string | undefined>();
    let [error, setError] = useState<string | undefined>();
    let [waitingPrompt, setWaitingPrompt] = useState<string | undefined>(props.initialPrompt);
    let [response, setResponse] = useState<OpenAIResponse<string> | undefined>(props.priorConversation);
    let [showPrePost, setShowPrePost] = useState<boolean>(props.pretext !== undefined || props.posttext !== undefined);

    useEffect(() => {
        if (props.initialPrompt && !response) {
            setWaitingPrompt(props.initialPrompt);
            processAI(props.initialPrompt);
        }
    }, [props.initialPrompt]);

    const innerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setTimeout(() => {
            if (innerRef.current) {
                const innerHeight = innerRef.current.scrollHeight;

                innerRef.current.scrollTo({
                    top: innerHeight,
                    left: 0,
                    behavior: "smooth",
                });
            }
        }, 300);
    }, [response, waitingPrompt]);

    function processAI(modifiedPrompt: string) {
        setWaitingPrompt(modifiedPrompt);

        setError(undefined);
        setPrompt("");
        setShowPrePost(false);

        AppGlueCorePlugins.OpenAIPlugin.chatWithAI(modifiedPrompt, undefined, response)
            .then((val) => {
                setWaitingPrompt(undefined);
                setResponse(val);
            })
            .catch((e) => {
                setWaitingPrompt(undefined);
                setError(e.message);
            });
    }

    return (
        <Box
            sx={{ display: "flex", flexDirection: "column" }}
            padding={"10px"}
            height={"100%"}
        >
            <Box
                height={"100%"}
                flexGrow={1}
                display={"flex"}
                flexDirection={"column"}
                padding={"5px"}
                border={"1px solid " + UIStyleConstants.GRAY_DARK_COLOR}
                sx={{ overflowY: "auto" }}
                ref={innerRef}
            >
                {response?.messages.map((r) => {
                    return (
                        <RenderResponse
                            key={r.id}
                            response={{ role: r.messageParam.role, content: r.messageParam.content?.toString() }}
                            onSelectAnswer={props.onSelectAnswer}
                            removeExplanation={() => {
                                processAI("without explanation");
                            }}
                            needsClean={props.needsClean}
                        ></RenderResponse>
                    );
                })}
                {waitingPrompt && (
                    <>
                        <UILabel color={UIStyleConstants.BLUE_COLOR}>Waiting on response to: </UILabel>
                        <UILabel paddingLeft={"30px"}>{waitingPrompt.substring(0, 400)} </UILabel>
                    </>
                )}
                {error && <UILabel color={UIStyleConstants.RED_COLOR}>{error} </UILabel>}

                {!response && !error && !waitingPrompt && <UILabel>Enter data and submit to AI</UILabel>}
            </Box>
            <Box
                display={"flex"}
                flexDirection={"column"}
            >
                {showPrePost && response === undefined && (
                    <Box
                        display={"flex"}
                        flexDirection={"row"}
                        justifyContent={"center"}
                        paddingBottom={"10px"}
                        alignItems={"center"}
                    >
                        <UILabel variant={"large"}>
                            {props.pretext} __________________{" "}
                            {props.posttext && props.posttext?.length > 250
                                ? props.posttext?.substring(0, 250) + "..."
                                : props.posttext}
                        </UILabel>
                        <Button
                            sx={{ textTransform: "none" }}
                            onClick={() => setShowPrePost(false)}
                        >
                            Clear
                        </Button>
                    </Box>
                )}
                <Box
                    width={"100%"}
                    paddingRight={"10px"}
                    paddingTop={"20px"}
                    display={"flex"}
                    flexDirection={"row"}
                >
                    <TextField
                        label={props.label ?? "Prompt"}
                        fullWidth={true}
                        multiline={true}
                        size={"small"}
                        value={prompt}
                        onChange={(e) => {
                            setPrompt(e.target.value);
                        }}
                    ></TextField>
                    <Box
                        paddingLeft={"10px"}
                        height={"30px"}
                        display={"flex"}
                        flexDirection={"row"}
                        justifyContent={"end"}
                    >
                        <Button
                            sx={{ textTransform: "none" }}
                            variant={"outlined"}
                            onClick={() => {
                                if (prompt) {
                                    let modifiedPrompt =
                                        (props.pretext ?? "") +
                                        " " +
                                        prompt +
                                        " " +
                                        (props.posttext?.substring(200) ?? "");

                                    if (!showPrePost || response !== undefined) modifiedPrompt = prompt;
                                    processAI(modifiedPrompt);
                                }
                            }}
                        >
                            {waitingPrompt ? "Waiting" : "Submit"}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

function RenderResponse(props: {
    onSelectAnswer?: (answer: string) => void;
    response: { role: "system" | "user" | "assistant" | "function" | "tool" | "developer"; content: string | undefined };
    removeExplanation: () => void;
    needsClean?: (text: string) => boolean;
}) {
    if (!props.response.content) props.response.content = "";

    let [selection, setSelection] = useState<{ start: number; end: number }>();
    let [text, setText] = useState<string>(props.response.content);
    return (
        <Box
            display={"flex"}
            flexDirection={"row"}
            paddingBottom={"10px"}
        >
            <Box width={"100px"}>
                <UILabel>{props.response.role === "user" ? "me" : "ai"}</UILabel>
            </Box>
            {props.response.role === "user" && (
                <UILabel>
                    {props.response.content.length > 400
                        ? props.response.content.substring(0, 400) + "..."
                        : props.response.content}
                </UILabel>
            )}
            {props.response.role !== "user" && (
                <TextField
                    onSelectCapture={(e) => {
                        // @ts-ignore
                        if (!e.target.selectionStart || e.target.selectionStart === e.target.selectionEnd) {
                            setSelection(undefined);
                        } else {
                            // @ts-ignore
                            setSelection({ start: e.target.selectionStart, end: e.target.selectionEnd });
                        }
                    }}
                    fullWidth={true}
                    multiline={true}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                ></TextField>
            )}

            {props.response.role === "assistant" && props.onSelectAnswer && (
                <Box
                    display={"flex"}
                    flexDirection={"column"}
                    paddingLeft={"10px"}
                    paddingRight={"10px"}
                >
                    <Button
                        sx={{ textTransform: "none" }}
                        variant={"outlined"}
                        size={"small"}
                        onClick={() => {
                            if (!props.onSelectAnswer) return;
                            if (selection) {
                                props.onSelectAnswer(
                                    (props.response.content ?? "").substring(selection.start, selection.end),
                                );
                            } else {
                                props.onSelectAnswer(props.response.content ?? "");
                            }
                        }}
                    >
                        Use
                    </Button>
                    {props.needsClean && props.needsClean(props.response.content) && (
                        <Button
                            sx={{ textTransform: "none" }}
                            size={"small"}
                            onClick={() => {
                                props.removeExplanation();
                            }}
                            fullWidth={true}
                        >
                            Clean
                        </Button>
                    )}
                </Box>
            )}
        </Box>
    );
}
